import * as React from "react";
import { css } from "@emotion/core";
import {
  Dialog,
  Button,
  Checkbox,
  Callout,
  Icon,
  Divider,
} from "@blueprintjs/core";
import { ValidationIssue } from "../utils/validation";

interface WarningDialogProps {
  isOpen: boolean;
  warnings: ValidationIssue[];
  onClose: () => void;
  onConfirm: () => void;
}

export default function WarningDialog({
  isOpen,
  warnings,
  onClose,
  onConfirm,
}: WarningDialogProps) {
  const [acknowledged, setAcknowledged] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setAcknowledged(false);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (acknowledged) {
      onConfirm();
    }
  };

  const groupedWarnings = React.useMemo(() => {
    const groups: Record<string, ValidationIssue[]> = {};
    warnings.forEach((warning) => {
      if (!groups[warning.category]) {
        groups[warning.category] = [];
      }
      groups[warning.category].push(warning);
    });
    return groups;
  }, [warnings]);

  const getCategoryTitle = (category: string) => {
    switch (category) {
      case "data_quality":
        return "Data Quality Issues";
      case "geometry":
        return "Geometry File Issues";
      case "files":
        return "File Issues";
      case "form":
        return "Form Issues";
      default:
        return "Issues";
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "data_quality":
        return "warning-sign";
      case "geometry":
        return "shapes";
      case "files":
        return "document";
      case "form":
        return "form";
      default:
        return "warning-sign";
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Upload Warnings"
      icon="warning-sign"
      className="bp6-dark"
      style={{ width: "600px" }}
    >
      <div
        css={css`
          padding: 1rem;
        `}
      >
        <Callout intent="warning" icon="warning-sign">
          <p>
            The following issues have been detected with your upload. While you
            can proceed, these issues may affect processing quality or results.
          </p>
        </Callout>

        <div
          css={css`
            margin: 1rem 0;
            max-height: 400px;
            overflow-y: auto;
          `}
        >
          {Object.entries(groupedWarnings).map(
            ([category, categoryWarnings], index) => (
              <div key={category}>
                {index > 0 && <Divider />}
                <div
                  css={css`
                    margin: 1rem 0;
                  `}
                >
                  <h4
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 0.5rem;
                      margin: 0 0 0.5rem 0;
                      color: var(--warning);
                    `}
                  >
                    <Icon icon={getCategoryIcon(category)} />
                    {getCategoryTitle(category)}
                  </h4>
                  <ul
                    css={css`
                      margin: 0;
                      padding-left: 1.5rem;
                    `}
                  >
                    {categoryWarnings.map((warning, warningIndex) => (
                      <li
                        key={warningIndex}
                        css={css`
                          margin-bottom: 0.5rem;
                          color: var(--neutral-2);
                        `}
                      >
                        {warning.message}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )
          )}
        </div>

        <div
          css={css`
            margin: 1rem 0;
            padding: 1rem;
            background-color: var(--neutral-6);
            border-radius: 0.25rem;
          `}
        >
          <Checkbox
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.currentTarget.checked)}
            label="I understand these issues and want to continue with the upload"
            css={css`
              margin: 0;
            `}
          />
        </div>
      </div>

      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
          padding: 1rem;
          border-top: 1px solid var(--neutral-5);
        `}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Button
          intent="warning"
          disabled={!acknowledged}
          onClick={handleConfirm}
          icon="cloud-upload"
        >
          Continue Upload
        </Button>
      </div>
    </Dialog>
  );
}
